import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";

import EvaluationAlerts from "@/components/EvaluationAlerts";
import GoogleProfilePrompt from "@/components/GoogleProfilePrompt";

import Landing from "@/pages/Landing";
import Auth from "@/pages/Auth";
import ForgotPassword from "@/pages/ForgotPassword";
import StudentDashboard from "@/pages/student/StudentDashboard";
import Solutions from "@/pages/student/Solutions";
import Referrals from "@/pages/student/Referrals";
import Plans from "@/pages/student/Plans";
import Checkout from "@/pages/student/Checkout";
import CaptureUpload from "@/pages/student/CaptureUpload";
import AttemptInstructions from "@/pages/student/AttemptInstructions";
import ResultView from "@/pages/ResultView";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import QuestionPapers from "@/pages/admin/QuestionPapers";
import PlansAdmin from "@/pages/admin/PlansAdmin";
import FailedEvaluations from "@/pages/admin/FailedEvaluations";
import UserPurchases from "@/pages/admin/UserPurchases";
import PaperSolutionPlayer from "@/pages/lms/PaperSolutionPlayer";
import PaperSolutionEditor from "@/pages/admin/PaperSolutionEditor";

const HomeRedirect = () => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Landing />;
  return <Navigate to="/dashboard" replace />;
};

const DashboardRedirect = () => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={user.role === "super_admin" ? "/admin" : "/student/plans"} replace />;
};

const Protected = ({ roles, children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/dashboard" replace />;
  return children;
};

function App() {
  return (
    <div className="App grain">
      <AuthProvider>
        <BrowserRouter>
          <GoogleProfilePrompt />
          <EvaluationAlerts />
          <Routes>
            <Route path="/" element={<HomeRedirect />} />
            <Route path="/login" element={<Auth mode="login" />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/register" element={<Auth mode="register" />} />
            <Route path="/dashboard" element={<DashboardRedirect />} />

            <Route path="/student" element={<Protected roles={["student"]}><StudentDashboard /></Protected>} />
            <Route path="/student/solutions" element={<Protected roles={["student"]}><Solutions /></Protected>} />
            <Route path="/student/solutions/subject/:subject" element={<Protected roles={["student"]}><Solutions /></Protected>} />
            <Route path="/student/solutions/paper/:paperId" element={<Protected roles={["student"]}><Solutions /></Protected>} />
            <Route path="/student/solutions/:courseId/:lessonId" element={<Protected roles={["student"]}><PaperSolutionPlayer /></Protected>} />
            <Route path="/student/referrals" element={<Protected roles={["student"]}><Referrals /></Protected>} />
            <Route path="/student/plans" element={<Protected roles={["student"]}><Plans /></Protected>} />
            <Route path="/student/checkout" element={<Protected roles={["student"]}><Checkout /></Protected>} />
            <Route path="/student/attempt/:paperId/instructions" element={<Protected roles={["student"]}><AttemptInstructions /></Protected>} />
            <Route path="/student/capture/:paperId" element={<Protected roles={["student"]}><CaptureUpload /></Protected>} />
            <Route path="/result/:scriptId" element={<Protected><ResultView /></Protected>} />

            <Route path="/admin" element={<Protected roles={["super_admin"]}><AdminDashboard /></Protected>} />
            <Route path="/admin/users" element={<Protected roles={["super_admin"]}><UserPurchases /></Protected>} />
            <Route path="/admin/plans" element={<Protected roles={["super_admin"]}><PlansAdmin /></Protected>} />
            <Route path="/admin/papers" element={<Protected roles={["super_admin"]}><QuestionPapers /></Protected>} />
            <Route path="/admin/papers/:paperId/solution" element={<Protected roles={["super_admin"]}><PaperSolutionEditor /></Protected>} />
            <Route path="/admin/failed-evaluations" element={<Protected roles={["super_admin"]}><FailedEvaluations /></Protected>} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
